import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startOfYear } from "date-fns";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";

import PercentIcon from "@mui/icons-material/Percent";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";

import PageContainer from "components/templates/PageContainer";
import InfoTooltip from "components/atoms/info/InfoTooltip";
import SelectBlock from "components/atoms/select/SelectBlock";
import ButtonDatePicker from "components/atoms/dateTime/ButtonDatePicker";
import { getFormattedDateURL } from "components/atoms/dateTime/GetFormattedDate";
import { SnackContext } from "context/SnackProvider";
import instance, { getErrorMessage } from "api/definitions";
import SelectLocationType from "components/atoms/select/SelectLocationType";
import ReportLocationOccupationTable from "components/molecules/table/ReportLocationOccupationTable";

const GetLocationOccupation = (props) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [items, setItems] = useState([]);

  const snackContext = useContext(SnackContext);

  useEffect(() => {
    setIsLoaded(false);
    const idBlock = props.idBlock ? "&idBloco=" + props.idBlock : "";
    const idLocationType = props.idLocationType
      ? "&idLocalTipo=" + props.idLocationType
      : "";
    const weekDays = "&weekDays=" + props.weekDays;
    const fullURL = `relatorio/localOcupacao?data1=${props.dateBegin}&data2=${props.dateEnd}${idBlock}${idLocationType}${weekDays}`;
    instance
      .get(fullURL)
      .then(function (response) {
        setIsLoaded(true);
        const result = response.data;
        setItems(result);
      })
      .catch(function (error) {
        setIsLoaded(true);
        const msg = getErrorMessage(error);
        snackContext.setMessage(msg, "error");
      });
  }, [
    props.dateBegin,
    props.dateEnd,
    props.idBlock,
    props.idLocationType,
    props.weekDays,
    snackContext,
  ]);

  if (!isLoaded) {
    return <CircularProgress />;
  } else {
    return <ReportLocationOccupationTable rows={items} />;
  }
};

const ReportLocationOccupation = () => {
  const [load, setLoad] = useState(false);
  const [block, setBlock] = useState(null);
  const [locationType, setLocationType] = useState(null);
  const [weekDays, setWeekDays] = useState(() => [1, 2, 3, 4, 5]);
  const [dateBegin, setDateBegin] = useState(startOfYear(new Date()));
  const [dateEnd, setDateEnd] = useState(new Date());

  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1);
  };

  const blockCallback = (childData) => {
    setLoad(false);
    setBlock(childData);
  };

  const locationTypeCallback = (childData) => {
    setLoad(false);
    setLocationType(childData);
  };

  const handleClick = () => {
    if (weekDays.length === 0) {
      alert("Escolha pelo menos um dia da semana.");
      setLoad(false);
    } else setLoad(true);
  };

  const handleChangeWeekDays = (event, newValue) => {
    setLoad(false);
    setWeekDays(newValue);
  };

  const handleChangeDateBegin = (newValue) => {
    setLoad(false);
    setDateBegin(newValue);
  };

  const handleChangeDateEnd = (newValue) => {
    setLoad(false);
    setDateEnd(newValue);
  };

  const handleClean = (event) => {
    setLoad(false);
    blockCallback(null);
    setDateEnd(new Date());
  };

  return (
    <PageContainer
      maxWidth="lg"
      title={"Ocupação dos Locais"}
      icon={<PercentIcon />}
    >
      <Box id="ControlButtons" sx={{ mt: 2 }}>
        <InfoTooltip placement="top-start" title="Voltar">
          <IconButton onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
        </InfoTooltip>
        <InfoTooltip title="Reiniciar filtros" placement="top">
          <IconButton variant="contained" onClick={handleClean}>
            <RefreshIcon />
          </IconButton>
        </InfoTooltip>
        <InfoTooltip title="Pesquisar" placement="top">
          <IconButton variant="contained" onClick={handleClick}>
            <SearchIcon />
          </IconButton>
        </InfoTooltip>
      </Box>
      <Box>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={4} sm={3} md={2}>
            <ButtonDatePicker
              value={dateBegin}
              onChange={handleChangeDateBegin}
              id="dateBegin"
              label="Data Início"
              name="dateBegin"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item xs={4} sm={3} md={2}>
            <ButtonDatePicker
              value={dateEnd}
              onChange={handleChangeDateEnd}
              id="dateEnd"
              label="Data Fim"
              name="dateEnd"
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={4} lg={3}>
            <SelectBlock
              dataParentToChild={block}
              parentCallback={blockCallback}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={4} lg={3}>
            <SelectLocationType
              dataParentToChild={locationType}
              parentCallback={locationTypeCallback}
            />
          </Grid>
          <Grid item>
            <Box sx={{ mt: 1 }}>
              <ToggleButtonGroup
                color="primary"
                value={weekDays}
                onChange={handleChangeWeekDays}
                aria-label="weekDays"
              >
                <ToggleButton
                  value={0}
                  aria-label="0"
                  sx={{ height: 50, p: 0 }}
                >
                  <Tooltip title="Domingo" followCursor>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6">D</Typography>
                    </Box>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value={1}
                  aria-label="1"
                  sx={{ height: 50, p: 0 }}
                >
                  <Tooltip title="Segunda" followCursor>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6">S</Typography>
                    </Box>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value={2}
                  aria-label="2"
                  sx={{ height: 50, p: 0 }}
                >
                  <Tooltip title="Terça" followCursor>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6">T</Typography>
                    </Box>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value={3}
                  aria-label="3"
                  sx={{ height: 50, p: 0 }}
                >
                  <Tooltip title="Quarta" followCursor>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6">Q</Typography>
                    </Box>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value={4}
                  aria-label="4"
                  sx={{ height: 50, p: 0 }}
                >
                  <Tooltip title="Quinta" followCursor>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6">Q</Typography>
                    </Box>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value={5}
                  aria-label="5"
                  sx={{ height: 50, p: 0 }}
                >
                  <Tooltip title="Sexta" followCursor>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6">S</Typography>
                    </Box>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton
                  value={6}
                  aria-label="6"
                  sx={{ height: 50, p: 0 }}
                >
                  <Tooltip title="Sábado" followCursor>
                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6">S</Typography>
                    </Box>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {load && (
        <Box sx={{ mt: 2 }}>
          <GetLocationOccupation
            dateBegin={getFormattedDateURL(dateBegin)}
            dateEnd={getFormattedDateURL(dateEnd)}
            idBlock={block && block.id}
            idLocationType={locationType && locationType.id}
            weekDays={weekDays}
          />
        </Box>
      )}
    </PageContainer>
  );
};

export default ReportLocationOccupation;
